'use client';

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { faFileExport } from '@fortawesome/pro-solid-svg-icons';
import { useGetDataIssuesQuery as useGetDataIssues } from 'src/api/redux';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import { useLogging } from 'src/context/logging';
import { useLayoutControl } from 'src/context/layout';
import { useNotification } from 'src/context/notification';
import {
  PulseHeader,
  PulseCell,
  PulseColumnMenu,
  PulseColumnDef,
  PulseLoading,
  PulseButton,
  Search,
} from 'src/components';
import { TableFooter } from 'src/components/Tables';
import { TableColumnMenu } from 'src/components/Tables/components/TableColumnMenu';
import { SortSection } from 'src/components/Activity/components/SortSection';
import { getExportDataIssues } from 'src/api/dataIssuesApi';

const DataIssueTable = () => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { setShowResolveDataIssueDialog } = useLayoutControl();

  // Column definitions
  const columns: PulseColumnDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Provider',
        width: 200,
        sortable: true,
        resizable: true,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: true,
        resizable: true,
      },
      {
        field: 'issue_type',
        headerName: 'Type',
        width: 150,
        sortable: true,
        resizable: true,
      },
      {
        field: 'reporting_user_email',
        headerName: 'Reported By',
        width: 200,
        sortable: true,
        resizable: true,
      },
      {
        field: 'reported_at',
        headerName: 'Reported At',
        width: 180,
        sortable: true,
        resizable: true,
      },
    ],
    []
  );

  // Column management
  const { startResizing, isResizing, getGridTemplateColumns } =
    usePulseTableColumns({ columns });

  // State
  const [isExporting, setIsExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [sort, setSort] = useState<{ field: string; order: 'asc' | 'desc' }>({
    field: 'reported_at',
    order: 'desc',
  });
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >(
    columns.reduce((acc, col) => {
      acc[col.field] = true;
      return acc;
    }, {} as Record<string, boolean>)
  );
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Visible columns
  const visibleColumns = useMemo(
    () => columns.filter((col) => columnVisibilityModel[col.field]),
    [columns, columnVisibilityModel]
  );

  // Fetch data
  const { data: { results: dataIssues = [], count = 0 } = {}, isLoading } =
    useGetDataIssues({
      q: searchTerm || null,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sort: sort.field,
      order: sort.order,
    });

  // Virtualization
  const ROW_HEIGHT = 48;
  const BUFFER_ROWS = 5;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const { visibleRows, startIndex, totalHeight } = useMemo(() => {
    if (!dataIssues.length) {
      return { visibleRows: [], startIndex: 0, totalHeight: 0 };
    }

    const totalHeight = dataIssues.length * ROW_HEIGHT;
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS
    );
    const visibleCount =
      Math.ceil(containerHeight / ROW_HEIGHT) + BUFFER_ROWS * 2;
    const endIndex = Math.min(dataIssues.length, startIndex + visibleCount);
    const visibleRows = dataIssues.slice(startIndex, endIndex);

    return { visibleRows, startIndex, totalHeight };
  }, [dataIssues, scrollTop, containerHeight]);

  // Handlers
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateHeight = () => {
      setContainerHeight(container.clientHeight);
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Close export menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(e.target as Node)
      ) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSortToggle = useCallback(
    (columnField: string, order?: 'asc' | 'desc') => {
      setSort((prevSort) => {
        const newOrder =
          order ||
          (prevSort.field === columnField
            ? prevSort.order === 'asc'
              ? 'desc'
              : 'asc'
            : 'desc');
        return { field: columnField, order: newOrder };
      });
      setOpenMenuId(null);
    },
    []
  );

  const handleEllipsisClick = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.stopPropagation();
      setOpenMenuId((prev) => (prev === columnId ? null : columnId));
    },
    []
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (_e: unknown, newPage: number) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPaginationModel({
      pageSize: parseInt(e.target.value, 10),
      page: 0,
    });
  };

  const handleRowClick = (id: string) => {
    setShowResolveDataIssueDialog({
      show: true,
      dataIssueId: id,
    });
  };

  const handleExportList = async (type: string) => {
    const visibleColumnFields = visibleColumns
      .map((col) => col.field)
      .concat(['claims', 'patients', 'trends']);

    setIsExporting(true);
    setShowExportMenu(false);

    try {
      const res = await getExportDataIssues({
        searchParams: {
          q: searchTerm || null,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sort: sort.field,
          order: sort.order,
        },
        fileType: type,
        columns: visibleColumnFields,
        fileName: `data-issues-${new Date()
          .toISOString()
          .replace(/[:.T]/g, '-')
          .slice(0, 16)}`,
      });

      if (!res) return;
      setNotification({
        title: 'Success',
        message:
          'Export initiated - You will receive an email when it is ready for download',
        type: 'success',
      });
      log.event('Export', {
        type: 'data-issues',
        source: 'Data Issues',
        columns: visibleColumnFields,
        count: dataIssues?.length,
      });
    } catch (err: any) {
      log.exception(`Error exporting List: ${err?.error_message}`);
      setNotification({
        title: 'Error',
        message: err?.error_message || 'Error exporting, please try again.',
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderCellValue = (row: any, field: string) => {
    const value = row[field];
    if (field === 'reported_at') {
      return formatDate(value);
    }
    return value || '-';
  };

  const hasData = dataIssues && dataIssues.length > 0;

  return (
    <div
      className="h-[calc(100vh-15rem)] w-full p-4 bg-white flex flex-col rounded-lg border border-neutral-200 overflow-hidden"
      data-testid="data-issue-table"
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 pb-4 border-b border-neutral-200 shrink-0">
        <div className="flex-1 max-w-md">
          <Search
            size="sm"
            placeholder="Search by Provider, Status, or Reporting User"
            onSearch={handleSearch}
            variant="outlined"
            className="rounded-full"
            data-testid="data-issue-search"
          />
        </div>
        <div className="flex items-center gap-2">
          {/* Export Menu */}
          <div ref={exportMenuRef} className="relative">
            <PulseButton
              variant="outline"
              color="neutral"
              size="sm"
              startDecorator={faFileExport}
              onClick={() => setShowExportMenu(!showExportMenu)}
              loading={isExporting}
              className="w-24 h-9 rounded-full"
              aria-label="Export data issues"
              aria-expanded={showExportMenu}
              aria-haspopup="menu"
              data-testid="data-issue-export-button"
            >
              Export
            </PulseButton>
            {showExportMenu && (
              <div
                className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-neutral-200 z-50"
                role="menu"
                aria-label="Export options"
                data-testid="data-issue-export-menu"
              >
                <button
                  className="w-full px-4 py-2 text-sm text-left hover:bg-neutral-100 rounded-t-lg"
                  onClick={() => handleExportList('csv')}
                  role="menuitem"
                  data-testid="data-issue-export-csv"
                >
                  Export as CSV
                </button>
                <button
                  className="w-full px-4 py-2 text-sm text-left hover:bg-neutral-100 rounded-b-lg"
                  onClick={() => handleExportList('xlsx')}
                  role="menuitem"
                  data-testid="data-issue-export-excel"
                >
                  Export as Excel
                </button>
              </div>
            )}
          </div>

          {/* Column Visibility Menu */}
          <TableColumnMenu
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            setColumnVisibilityModel={setColumnVisibilityModel}
            table="data-issues"
          />
        </div>
      </div>

      {/* Table Container */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-auto relative min-h-0"
        onScroll={handleScroll}
      >
        {/* Header Row */}
        <div
          className="sticky top-0 z-20 grid min-w-max"
          style={{ gridTemplateColumns: getGridTemplateColumns() }}
        >
          {visibleColumns.map((col) => {
            const isMenuOpen = openMenuId === col.field;
            const isActiveSortColumn = sort.field === col.field;

            return (
              <PulseHeader
                key={col.field}
                label={col.headerName}
                isSticky
                stickyPosition="top"
                className="bg-neutral-50 text-neutral-800 border-b border-r border-b-neutral-200 border-r-neutral-200"
                sortable={col.sortable}
                sortDirection={isActiveSortColumn ? sort.order : null}
                resizable={col.resizable}
                onStartResize={
                  col.resizable ? (e) => startResizing(col.field, e) : undefined
                }
                isResizing={isResizing}
                menu={
                  col.sortable ? (
                    <PulseColumnMenu
                      columnId={col.field}
                      isOpen={isMenuOpen}
                      onToggle={(e) => handleEllipsisClick(e, col.field)}
                      customSectionComponents={[
                        <SortSection
                          key="sort"
                          currentOrder={sort.order}
                          isActive={isActiveSortColumn}
                          onChange={(order) =>
                            handleSortToggle(col.field, order)
                          }
                        />,
                      ]}
                    />
                  ) : undefined
                }
              />
            );
          })}
          {/* Filler Header */}
          <div className="h-12 bg-neutral-50 border-b border-b-neutral-200" />
        </div>

        {/* Data Rows */}
        {hasData && (
          <div
            role="table"
            aria-label="Data issues table"
            className="relative min-w-max"
            style={{ height: totalHeight }}
            data-testid="data-issue-table-body"
          >
            {visibleRows.map((row: any, index: number) => (
              <div
                key={row.id}
                role="row"
                aria-label={`Data issue for ${row.name || 'unknown provider'}`}
                className="absolute left-0 right-0 grid group cursor-pointer hover:bg-neutral-50"
                style={{
                  top: (startIndex + index) * ROW_HEIGHT,
                  height: ROW_HEIGHT,
                  gridTemplateColumns: getGridTemplateColumns(),
                }}
                onClick={() => handleRowClick(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(row.id);
                  }
                }}
                tabIndex={0}
                data-testid={`data-issue-row-${row.id}`}
              >
                {visibleColumns.map((col) => (
                  <PulseCell
                    key={col.field}
                    className="bg-white border-b border-r border-b-neutral-200 border-r-neutral-200 group-hover:bg-neutral-50"
                  >
                    <span className="truncate">
                      {renderCellValue(row, col.field)}
                    </span>
                  </PulseCell>
                ))}
                {/* Filler Cell */}
                <div className="h-12 bg-white border-b border-b-neutral-200 group-hover:bg-neutral-50" />
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!hasData && !isLoading && (
          <div
            className="flex justify-center items-center h-64"
            role="status"
            aria-live="polite"
            data-testid="data-issue-empty-state"
          >
            <div className="text-neutral-600 text-base font-medium">
              No data issues found
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {isLoading && <PulseLoading />}
      </div>

      {/* Footer */}
      <div className="shrink-0">
        <TableFooter
          rowCount={count}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
};

export default DataIssueTable;
